"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSetAtom } from "jotai";
import { MilkdownProvider } from "@milkdown/react";
import {
  ChevronUp,
  ChevronsDownUp,
  ChevronsUpDown,
  Code,
  EllipsisVertical,
  GripVertical,
  Plus,
  Trash2,
} from "lucide-react";
import type { Edge } from "@xyflow/react";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { ComplexMarkdownEditor } from "@/components/Form/ComplexMarkdownField/ComplexMarkdownField";
import type { EditorInput } from "@/components/Form/ComplexMarkdownField";
import { CodeInput } from "@/components/Form/CodeField";
import {
  NoxusFormLabel,
  PinFieldContext,
} from "@/components/Form/NoxusFormLabel";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { edgesAtom } from "@/store/editor-v2";
import { cn } from "@/lib/utils";
import { useUpstreamVariableOptions } from "./InlineVariablePicker";
import { nodeOptionsToEditorInputs } from "./editorInputsFromOptions";
import { nextDefaultRouteName } from "./routeDefaultName";
import {
  parseRoutesJson,
  serializeRoutes,
  type RouteRow,
} from "./routeJsonMode";

function newId(): string {
  return crypto.randomUUID();
}

const blankRoute = (name: string): RouteRow => ({
  id: newId(),
  name,
  condition: "",
});

function routeSortId(row: RouteRow, index: number): string {
  return row.id || `index-${index}`;
}

function RoutesJsonModeEditor({
  rows,
  disabled,
  onCancel,
  onSubmit,
}: {
  rows: RouteRow[];
  disabled?: boolean;
  onCancel: () => void;
  onSubmit: (rows: RouteRow[]) => void;
}) {
  const [jsonValue, setJsonValue] = useState(() => serializeRoutes(rows));
  const [errors, setErrors] = useState<string[]>([]);

  const validateAndSubmit = useCallback(() => {
    const { routes, errors: validationErrors } = parseRoutesJson(
      jsonValue,
      newId
    );
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors([]);
    onSubmit(routes);
  }, [jsonValue, onSubmit]);

  return (
    <div className="flex flex-col gap-3">
      <CodeInput
        value={jsonValue}
        onChange={setJsonValue}
        isDisabled={disabled}
        placeholder='[{"id":"…","name":"Urgent","condition":"…"}]'
        language="json"
      />

      {errors.length > 0 ? (
        <div className="rounded-md border border-destructive bg-destructive/10 p-3">
          <p className="mb-2 text-sm font-medium text-destructive">
            Validation Errors:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {errors.map((error, index) => (
              <li key={index} className="text-destructive">
                {error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={disabled}>
          Cancel
        </Button>
        <Button onClick={validateAndSubmit} disabled={disabled}>
          Apply Changes
        </Button>
      </div>
    </div>
  );
}

function SortableRouteItem({
  row,
  sortId,
  accordionValue,
  inputs,
  nodeId,
  isOpen,
  canDelete,
  disabled,
  onPatch,
  onNameBlur,
  onRemove,
}: {
  row: RouteRow;
  sortId: string;
  accordionValue: string;
  inputs: EditorInput[];
  nodeId: string;
  isOpen: boolean;
  canDelete: boolean;
  disabled?: boolean;
  onPatch: (patch: Partial<RouteRow>) => void;
  onNameBlur: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: sortId, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const displayName = row.name?.trim() || "New Route";

  return (
    <div ref={setNodeRef} style={style} className="w-full min-w-0">
      <AccordionItem
        value={accordionValue}
        className="border-0 first:border-t-0 last:border-b-0"
      >
        <div className="group w-full min-w-0 rounded-lg border border-border bg-background px-4">
          <div className="flex w-full items-center gap-2 [&>h3]:min-w-0 [&>h3]:flex-1">
            <div
              {...attributes}
              {...listeners}
              className="flex shrink-0 cursor-grab items-center active:cursor-grabbing"
            >
              <GripVertical className="size-4 text-muted-foreground" />
            </div>
            <AccordionTrigger
              className="w-full min-w-0 items-center overflow-visible py-3 hover:no-underline"
              hideChevron
            >
              <div className="flex w-full items-center justify-between gap-2 pr-2">
                <span className="truncate text-sm font-medium leading-none">
                  {displayName}
                </span>
                <div className="flex shrink-0 items-center gap-2">
                  <div className="hidden h-6 items-center group-hover:flex">
                    <Button
                      type="button"
                      variant="outline"
                      size="iconxs"
                      disabled={disabled || !canDelete}
                      className="rounded-full text-muted-foreground"
                      title={
                        canDelete
                          ? "Remove route"
                          : "At least two routes are required"
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                      }}
                    >
                      <Trash2 className="size-3" strokeWidth={1.25} />
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="iconsm"
                    tabIndex={-1}
                    className="shrink-0"
                  >
                    <ChevronUp
                      className={cn(
                        "size-4 transition-transform duration-200",
                        !isOpen && "rotate-180"
                      )}
                    />
                  </Button>
                </div>
              </div>
            </AccordionTrigger>
          </div>

          <AccordionContent className="w-full border-t pb-4 pt-3">
            <div className="flex w-full flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <NoxusFormLabel label="Route name" noMargin />
                <Input
                  disabled={disabled}
                  value={row.name ?? ""}
                  placeholder="Name of this route (e.g. 'Urgent', 'Normal')"
                  onChange={(event) => onPatch({ name: event.target.value })}
                  onBlur={onNameBlur}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <NoxusFormLabel label="Condition" noMargin />
                <MilkdownProvider>
                  <ComplexMarkdownEditor
                    value={row.condition ?? ""}
                    onChange={(value) => onPatch({ condition: value })}
                    placeholder="When should this route be taken? (mention variables inline)…"
                    isDisabled={disabled}
                    inputs={inputs}
                    nodeId={nodeId}
                  />
                </MilkdownProvider>
              </div>
            </div>
          </AccordionContent>
        </div>
      </AccordionItem>
    </div>
  );
}

export function BindableRoutesField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  error,
  isRequired,
  showOptionalLabel,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const rows: RouteRow[] = useMemo(
    () => (Array.isArray(raw) ? raw : []),
    [raw]
  );
  const setEdges = useSetAtom(edgesAtom);
  const normalizedNamesRef = useRef(false);
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [isJsonMode, setIsJsonMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const options = useUpstreamVariableOptions({ inlineText: true });
  const inputs = useMemo(() => nodeOptionsToEditorInputs(options), [options]);

  const sortIds = useMemo(
    () => rows.map((row, index) => routeSortId(row, index)),
    [rows]
  );

  const write = useCallback(
    (next: RouteRow[]) =>
      form.setValue(path, next, { shouldDirty: true, shouldTouch: true }),
    [form, path]
  );

  useEffect(() => {
    if (rows.length === 0) return;
    if (rows.every((row) => typeof row.id === "string" && row.id)) return;
    form.setValue(
      path,
      rows.map((row) => (row.id ? row : { ...row, id: newId() })),
      { shouldDirty: false, shouldTouch: false }
    );
  }, [rows, form, path]);

  useEffect(() => {
    normalizedNamesRef.current = false;
  }, [path]);

  useEffect(() => {
    if (normalizedNamesRef.current || rows.length === 0) return;
    if (rows.every((row) => row.name?.trim())) {
      normalizedNamesRef.current = true;
      return;
    }

    normalizedNamesRef.current = true;
    const usedNames: string[] = [];
    const next = rows.map((row) => {
      const trimmed = row.name?.trim() ?? "";
      if (trimmed) {
        usedNames.push(trimmed);
        return row;
      }
      const name = nextDefaultRouteName(usedNames);
      usedNames.push(name);
      return { ...row, name };
    });

    form.setValue(path, next, { shouldDirty: false, shouldTouch: false });
  }, [rows, form, path]);

  const patchRow = (index: number, patch: Partial<RouteRow>) => {
    const next = rows.slice();
    next[index] = { ...next[index], ...patch };
    write(next);
  };

  const ensureRouteName = (index: number) => {
    if (rows[index]?.name?.trim()) return;
    const name = nextDefaultRouteName(
      rows.map((row, rowIndex) => (rowIndex === index ? "" : (row.name ?? "")))
    );
    patchRow(index, { name });
  };

  const addRow = () => {
    const name = nextDefaultRouteName(rows.map((row) => row.name ?? ""));
    const route = blankRoute(name);
    write([...rows, route]);
    if (route.id) {
      setOpenItems((prev) => [...prev, route.id!]);
    }
  };

  const removeRow = (index: number) => {
    const removed = rows[index];
    const sortId = sortIds[index];
    write(rows.filter((_, rowIndex) => rowIndex !== index));
    setOpenItems((prev) => prev.filter((item) => item !== sortId));
    if (removed?.id) {
      setEdges((prev: Edge[]) =>
        prev.filter(
          (edge) =>
            !(edge.source === nodeId && edge.sourceHandle === removed.id)
        )
      );
    }
  };

  const handleExpandAll = useCallback(() => {
    setOpenItems(sortIds);
  }, [sortIds]);

  const handleCollapseAll = useCallback(() => {
    setOpenItems([]);
  }, []);

  const handleDragStart = useCallback(() => {
    setOpenItems([]);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || disabled) return;

      const oldIndex = sortIds.indexOf(String(active.id));
      const newIndex = sortIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      write(arrayMove(rows, oldIndex, newIndex));
    },
    [disabled, rows, sortIds, write]
  );

  const handleJsonSubmit = useCallback(
    (next: RouteRow[]) => {
      write(next);
      setIsJsonMode(false);
      setOpenItems([]);
    },
    [write]
  );

  const canDelete = rows.length > 2;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <NoxusFormLabel
            label={label}
            tooltip={tooltip}
            name={fieldKey}
            isDisabled={disabled}
            isRequired={isRequired}
            showOptionalLabel={showOptionalLabel}
            noMargin
          />
        </div>
        {!isJsonMode && rows.length > 0 ? (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={disabled}
                className="shrink-0"
              >
                <EllipsisVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExpandAll}>
                <ChevronsUpDown />
                Expand All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCollapseAll}>
                <ChevronsDownUp />
                Collapse All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsJsonMode(true)}>
                <Code className="size-4" />
                JSON Mode
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <PinFieldContext.Provider value={{}}>
        {isJsonMode ? (
          <RoutesJsonModeEditor
            rows={rows}
            disabled={disabled}
            onCancel={() => setIsJsonMode(false)}
            onSubmit={handleJsonSubmit}
          />
        ) : (
          <div
            className={cn(
              "flex w-full min-w-0 flex-col gap-3",
              error && "rounded-md p-1.5 ring-1 ring-inset ring-amber-400/70"
            )}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={sortIds}
                strategy={verticalListSortingStrategy}
              >
                <Accordion
                  type="multiple"
                  value={openItems}
                  onValueChange={setOpenItems}
                  className="flex w-full min-w-0 flex-col gap-3"
                >
                  {rows.map((row, index) => {
                    const sortId = sortIds[index];
                    return (
                      <SortableRouteItem
                        key={sortId}
                        row={row}
                        sortId={sortId}
                        accordionValue={sortId}
                        inputs={inputs}
                        nodeId={nodeId}
                        isOpen={openItems.includes(sortId)}
                        canDelete={canDelete}
                        disabled={disabled}
                        onPatch={(patch) => patchRow(index, patch)}
                        onNameBlur={() => ensureRouteName(index)}
                        onRemove={() => removeRow(index)}
                      />
                    );
                  })}
                </Accordion>
              </SortableContext>
            </DndContext>

            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className="w-full"
              onClick={addRow}
            >
              <Plus className="mr-1 size-4" /> Add route
            </Button>
          </div>
        )}
      </PinFieldContext.Provider>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}
